import React, { useState } from "react";
import GuideData from "../../Data/GuideData";
import {
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

export default function Guide() {
  const theme = useTheme();
  const [selectedIssueValues, setSelectedIssueValues] = useState<number>(0);

  return (
    <Box sx={{ display: "flex", width: "100%", height: "100%", gap: "20px", p: 1 }}>
      {/* Left Column: List of Issues */}
      <Box sx={{ width: "320px", display: "flex", flexDirection: "column", gap: "12px", flexShrink: 0 }}>
        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ padding: "16px", borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ color: "text.primary", fontSize: "15px", fontWeight: 700 }}>
              Troubleshooting Issues
            </Typography>
          </Box>

          <List
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              padding: "8px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              "&::-webkit-scrollbar": {
                width: "5px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "divider",
                borderRadius: "4px",
              },
            }}
          >
            {GuideData?.map((items: any, index: number) => {
              const isSelected = selectedIssueValues === index;
              return (
                <ListItemButton
                  key={index}
                  selected={isSelected}
                  onClick={() => setSelectedIssueValues(index)}
                  sx={{
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    backgroundColor: isSelected
                      ? theme.palette.mode === "light" ? "#f0f7ff" : "#1e3a8a33"
                      : "background.paper",
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.mode === "light" ? "#f0f7ff" : "#1e3a8a33",
                      "&:hover": {
                        backgroundColor: theme.palette.mode === "light" ? "#e0f2fe" : "#1e3a8a50",
                      }
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b80",
                    },
                    transition: "all 0.15s ease",
                    py: 1.5,
                  }}
                >
                  <ListItemText
                    primary={items?.issueName}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "text.primary",
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Box>

      {/* Right Column: Detailed Solutions */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Paper
          variant="outlined"
          sx={{
            flexGrow: 1,
            borderRadius: "12px",
            borderColor: "divider",
            backgroundColor: "background.paper",
            p: 3,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            "&::-webkit-scrollbar": {
              width: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "divider",
              borderRadius: "4px",
            },
          }}
        >
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <HelpOutlineIcon color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary" }}>
                {GuideData[selectedIssueValues]?.issueName}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Recommended troubleshooting actions and procedures
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Solutions list */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {GuideData[selectedIssueValues]?.issueSolutions?.map((solution: any, idx: number) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "16px",
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b30",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <CheckCircleOutlineIcon color="success" sx={{ mt: "2px", flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                    {solution?.issueSolutionTitle}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
                    {solution?.issueSolutionDescription}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
